import { useEffect, useMemo, useState } from "react";
import { Box, Button, ClickAwayListener, Paper, Popper } from "@mui/material";
import { CalendarMonth, KeyboardArrowDown } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { getInitialRange, getTriggerLabel } from "./RangeCalendar/utils";
import { useRangeCalendar } from "./RangeCalendar/useRangeCalendar";
import RangeCalendar from "./RangeCalendar";

export default function DateRangePicker({ value, label, onApply }) {
  const initialRange = useMemo(() => getInitialRange(value), [value]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [committedRange, setCommittedRange] = useState(initialRange);

  const { selectedShortcut } = useRangeCalendar({ value });

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = (range) => {
    onApply?.(range);
    handleClose();
  };

  // Đồng bộ committedRange với giá trị ngoài
  useEffect(() => {
    const nextRange = getInitialRange(value);
    setCommittedRange(nextRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.fromDate, value?.toDate, value?.startDate, value?.endDate]);

  const triggerLabel = useMemo(
    () =>
      getTriggerLabel({
        label,
        selectedShortcut,
        committedRange,
      }),
    [label, selectedShortcut, committedRange],
  );

  const popperModifiers = useMemo(
    () => [
      {
        name: "preventOverflow",
        options: {
          boundary: "viewport",
          padding: 8,
          altAxis: true,
        },
      },
      {
        name: "flip",
        options: {
          fallbackPlacements: ["top-start", "bottom-end", "top-end"],
        },
      },
    ],
    [],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ClickAwayListener onClickAway={() => open && handleClose()}>
        <Box sx={{ display: "inline-flex" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpen}
            startIcon={<CalendarMonth fontSize="small" />}
            endIcon={<KeyboardArrowDown fontSize="small" />}
            sx={{
              height: "37px",
              textTransform: "none",
              borderColor: "var(--grey)",
              color: "var(--black)",
              minWidth: "150px",
              justifyContent: "space-between",
            }}
          >
            {triggerLabel}
          </Button>

          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            modifiers={popperModifiers}
            sx={{ zIndex: 1300, mt: 1 }}
          >
            <Paper
              elevation={8}
              sx={{
                width: 900,
                maxWidth: "min(900px, calc(100vw - 24px))",
                maxHeight: "min(90vh, calc(100dvh - 48px))",
                p: 1.5,
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <RangeCalendar
                value={value}
                onApply={handleApply}
                onClose={handleClose}
              />
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
    </LocalizationProvider>
  );
}
